import React from "react";
import PropTypes from "prop-types";
import { Whisper, Tooltip, Button } from "rsuite";
import PropertyIcon from "../../assets/img/property.png";
import StayDatesIcon from "../../assets/img/stay-dates.png";
import CityIcon from "../../assets/img/city.png";
import AdultsIcon from "../../assets/img/adults.png";
import ChildrenIcon from "../../assets/img/children.png";
import { format } from "date-fns";

const PriceCheckSummary = ({ formValues, properties, setIsDrawerOpen }) => {
  // Determine which field to show based on what's selected
  const hasProperties = formValues?.selectedProperties?.length > 0;
  const hasCities = formValues?.selectedCity?.length > 0;

  // Show property if properties are selected, otherwise show city if cities are selected
  const showProperty = hasProperties;
  const showCity = !hasProperties && hasCities;

  const propertyLabel = showProperty
    ? properties.find(
        (p) => p.propertyId === formValues?.selectedProperties?.[0]
      )?.propertyName || ""
    : "";
  const propertyCount = formValues?.selectedProperties?.length || 0;
  const cityLabel = showCity ? formValues?.selectedCity?.[0] : "";
  let stayDates = "";
  let stayDatesExtra = null;
  if (formValues?.dateRanges?.length > 0) {
    const formatRange = (range) =>
      `${format(new Date(range[0]), "MMM-dd-yyyy")} - ${format(new Date(range[1]), "MMM-dd-yyyy")}`;
    stayDates = formatRange(formValues.dateRanges[0]);
    if (formValues.dateRanges.length > 1) {
      stayDatesExtra = (
        <Whisper
          placement="bottom"
          trigger="hover"
          speaker={
            <Tooltip className="summary-tooltip">
              <ul className="summary-tooltip-list">
                {formValues?.dateRanges?.slice(1).map((range) => (
                  <li key={`date-range-${range[0]}-${range[1]}`}>
                    {formatRange(range)}
                  </li>
                ))}
              </ul>
            </Tooltip>
          }
        >
          <span className="summary-extra">
            +{formValues?.dateRanges?.length - 1}
          </span>
        </Whisper>
      );
    }
  }
  return (
    <div className="summary-row">
      {showProperty && (
        <span className="summary-field">
          <img
            src={PropertyIcon}
            alt="property icon"
            className="property-icon"
          />
          <div className="summary-container">
            <span className="summary-label">Property</span>
            <div className="summary-bottom">
              <span className="summary-value">{propertyLabel}</span>
              {propertyCount > 1 && (
                <Whisper
                  placement="bottom"
                  trigger="hover"
                  speaker={
                    <Tooltip className="summary-tooltip">
                      <ul className="summary-tooltip-list">
                        {formValues?.selectedProperties?.slice(1).map((id) => {
                          const property = properties.find(
                            (p) => p.propertyId === id
                          );
                          return (
                            <li key={id}>
                              {property ? property.propertyName : id}
                            </li>
                          );
                        })}
                      </ul>
                    </Tooltip>
                  }
                >
                  <span className="summary-extra">+{propertyCount - 1}</span>
                </Whisper>
              )}
            </div>
          </div>
        </span>
      )}
      <span className="summary-field">
        <img
          src={StayDatesIcon}
          alt="stay dates icon"
          className="stay-dates-icon"
        />
        <div className="summary-container">
          <span className="summary-label">Stay dates</span>
          <div className="summary-bottom">
            <span className="summary-value">{stayDates}</span>
            {stayDatesExtra}
          </div>
        </div>
      </span>
      {showCity && (
        <span className="summary-field">
          <img src={CityIcon} alt="city icon" className="city-icon" />
          <div className="summary-container">
            <span className="summary-label">City</span>
            <div className="summary-bottom">
              <span className="summary-value">{cityLabel}</span>
              {formValues?.selectedCity?.length > 1 && (
                <Whisper
                  placement="bottom"
                  trigger="hover"
                  speaker={
                    <Tooltip className="summary-tooltip">
                      <ul className="summary-tooltip-list">
                        {formValues?.selectedCity?.slice(1).map((city, idx) => (
                          <li key={city}>{city}</li>
                        ))}
                      </ul>
                    </Tooltip>
                  }
                >
                  <span className="summary-extra">
                    +{formValues?.selectedCity?.length - 1}
                  </span>
                </Whisper>
              )}
            </div>
          </div>
        </span>
      )}
      <span className="summary-field">
        <img src={AdultsIcon} alt="adults icon" className="adults-icon" />
        <div className="summary-container">
          <span className="summary-label">Adults</span>
          <div className="summary-bottom">
            <span className="summary-value">{formValues?.adults}</span>
          </div>
        </div>
      </span>
      <span className="summary-field">
        <img src={ChildrenIcon} alt="children icon" className="children-icon" />
        <div className="summary-container">
          <span className="summary-label">Children</span>
          <div className="summary-bottom">
            <span className="summary-value">{formValues?.children}</span>
          </div>
        </div>
      </span>
      <Button
        appearance="ghost"
        className="edit-search-btn"
        onClick={() => setIsDrawerOpen(true)}
      >
        Edit search
      </Button>
    </div>
  );
};

PriceCheckSummary.propTypes = {
  formValues: PropTypes.shape({
    selectedProperties: PropTypes.arrayOf(PropTypes.string).isRequired,
    selectedCity: PropTypes.arrayOf(PropTypes.string).isRequired,
    dateRanges: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    ).isRequired,
    adults: PropTypes.number.isRequired,
    children: PropTypes.number.isRequired,
  }).isRequired,
  properties: PropTypes.arrayOf(
    PropTypes.shape({
      propertyId: PropTypes.string.isRequired,
      propertyName: PropTypes.string.isRequired,
    })
  ).isRequired,
  setIsDrawerOpen: PropTypes.func.isRequired,
};

export default PriceCheckSummary;
